import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Shield, TrendingUp, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FinanceV2Section } from './FinanceV2Section';
import { MarkdownContent } from '../ui/markdown-content';
import { formatValue } from '../../utils/numberFormat';

/**
 * Local Smart Insights renderer for structured lens blocks. Strengths /
 * watchouts / risks columns are populated from the backend
 * ``performance_lens`` block payload.
 *
 * Display contract — no FE-side parsing of column or key names; those are
 * domain identifiers that belong to the backend payload. The renderer
 * surfaces three readable layers (headline, rationale, supporting
 * numbers strip) by default. Raw data references (dimensions, measures,
 * fields_used) sit behind a single "Show inputs" disclosure with an
 * honest framing that they are audit identifiers, not display labels.
 */
type LensKind = 'strengths' | 'watchouts' | 'risks';
type LensItem = Record<string, unknown>;
const PERFORMANCE_LENS_STACK_WIDTH = 760;

interface PerformanceLensSectionProps {
  data?: Record<string, unknown> | null;
  title?: string;
  framed?: boolean;
}

const bucketConfig: Record<LensKind, {
  title: string;
  blurb: string;
  Icon: typeof TrendingUp;
  rule: string;
  icon: string;
  valueLabel: string;
  stripe: string;
  chipBg: string;
  chipText: string;
  countBg: string;
  countText: string;
  expandedAccent: string;
}> = {
  strengths: {
    title: 'Strengths',
    blurb: 'At or above plan / LY. Protect and amplify.',
    Icon: TrendingUp,
    rule: 'bg-[#3F7D4F]',
    icon: 'text-[#3F7D4F]',
    valueLabel: 'Value',
    stripe: 'bg-gradient-to-b from-[#3F7D4F] to-[#5C9A6A]',
    chipBg: 'bg-[#ECF3EE]',
    chipText: 'text-[#2E5F3B]',
    countBg: 'bg-[#ECF3EE]',
    countText: 'text-[#3F7D4F]',
    expandedAccent: 'text-[#3F7D4F]',
  },
  watchouts: {
    title: 'Watchouts',
    blurb: 'Recoverable via normal-course execution.',
    Icon: TriangleAlert,
    rule: 'bg-[#C98661]',
    icon: 'text-[#B46647]',
    valueLabel: 'At stake',
    stripe: 'bg-gradient-to-b from-[#C98661] to-[#B46647]',
    chipBg: 'bg-[#FBEEDC]',
    chipText: 'text-[#8B4A2B]',
    countBg: 'bg-[#FBEEDC]',
    countText: 'text-[#B46647]',
    expandedAccent: 'text-[#B46647]',
  },
  risks: {
    title: 'Risks',
    blurb: 'Structural exposure. Escalate with a plan.',
    Icon: Shield,
    rule: 'bg-[#B23A48]',
    icon: 'text-[#B23A48]',
    valueLabel: 'At stake',
    stripe: 'bg-gradient-to-b from-[#B23A48] to-[#8E2A36]',
    chipBg: 'bg-[#FBE7E7]',
    chipText: 'text-[#7E1D29]',
    countBg: 'bg-[#FBE7E7]',
    countText: 'text-[#B23A48]',
    expandedAccent: 'text-[#B23A48]',
  },
};

// ---------------------------------------------------------------------------
// Display formatting
//
// No FE-side parsing of column / key names — labeling and unit hints are
// the backend's responsibility. The helpers here only do generic value
// formatting (number → locale string, string passthrough). Raw keys and
// raw column names are rendered as-is; if a key reads `total_change_pct`
// in the UI today, the fix lives in the BE payload, not this file.
// ---------------------------------------------------------------------------

function formatSupportingValueGeneric(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    const text = String(value);
    return text.length > 80 ? `${text.slice(0, 79)}…` : text;
  }
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function asItems(value: unknown): LensItem[] {
  return Array.isArray(value)
    ? value.filter((item): item is LensItem => Boolean(item && typeof item === 'object'))
    : [];
}

function bucketItems(data?: Record<string, unknown> | null): Record<LensKind, LensItem[]> {
  const buckets: Record<LensKind, LensItem[]> = {
    strengths: asItems(data?.strengths),
    watchouts: asItems(data?.watchouts),
    risks: asItems(data?.risks),
  };

  const flatItems = asItems(data?.items);
  const blockKind = String(data?.kind || '').toLowerCase() as LensKind;

  flatItems.forEach((item) => {
    const itemKind = String(item.kind || item.type || blockKind || '').toLowerCase();
    const normalizedKind: LensKind = itemKind.includes('risk')
      ? 'risks'
      : itemKind.includes('watch')
        ? 'watchouts'
        : 'strengths';
    buckets[normalizedKind].push(item);
  });

  return buckets;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,%\s,]/g, ''));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatLensValue(value: unknown, unit?: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return '';
  // Lens items default to USD when no unit is supplied (matches prior behavior).
  const normalizedUnit = String(unit || '').trim() || '$';
  return formatValue(numeric, { unit: normalizedUnit });
}

function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      setWidth(Math.round(element.getBoundingClientRect().width));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

function MiniSpark({ visual }: { visual: LensItem }) {
  const cleanValues = Array.isArray(visual.values)
    ? visual.values.map(toFiniteNumber).filter((value): value is number => value !== null)
    : [];
  if (cleanValues.length < 2) return null;
  const min = Math.min(...cleanValues);
  const max = Math.max(...cleanValues);
  const range = max - min || 1;
  const points = cleanValues.map((value, index) => {
    const x = (index / Math.max(1, cleanValues.length - 1)) * 100;
    const y = 28 - ((value - min) / range) * 24;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="mt-2">
      <svg viewBox="0 0 100 32" role="img" aria-label={String(visual.label || 'trend')} className="h-8 w-full overflow-visible">
        <polyline points={points} fill="none" stroke="#4E7C89" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {visual.label && <div className="mt-0.5 text-[10.5px] italic text-[#7B6B72]">{String(visual.label)}</div>}
    </div>
  );
}

function DeltaVisual({ visual }: { visual: LensItem }) {
  const current = toFiniteNumber(visual.current);
  const comparator = toFiniteNumber(visual.comparator);
  if (current === null && comparator === null) return null;
  const max = Math.max(Math.abs(current || 0), Math.abs(comparator || 0), 1);
  const pct = comparator && current !== null ? ((current - comparator) / Math.abs(comparator)) * 100 : null;

  return (
    <div className="mt-2 rounded-[8px] border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
          {String(visual.label || 'Current vs comparator')}
        </span>
        {pct !== null && Number.isFinite(pct) && (
          <span className="font-mono text-[12px] text-[#1F1A20]">{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
        )}
      </div>
      {[
        { label: 'Current', value: current, primary: true },
        { label: String(visual.comparator_label || 'Comparator'), value: comparator, primary: false },
      ].filter((row) => row.value !== null).map((row) => (
        <div key={row.label} className="grid grid-cols-[72px_1fr_76px] items-center gap-2 py-0.5 text-[11.5px]">
          <span className="truncate text-[#7B6B72]">{row.label}</span>
          <div className="h-[6px] overflow-hidden rounded-full border border-[#EDE6E1] bg-white">
            <div
              className={cn('h-full rounded-full', row.primary ? 'bg-[#4E7C89]' : 'bg-[#A8989F]/70')}
              style={{ width: `${Math.max(2, Math.min(100, (Math.abs(row.value || 0) / max) * 100))}%` }}
            />
          </div>
          <span className="truncate text-right font-mono text-[#3F3338]">{formatLensValue(row.value, visual.unit)}</span>
        </div>
      ))}
    </div>
  );
}

function MinibarVisual({ visual }: { visual: LensItem }) {
  const entries = asItems(visual.entries).slice(0, 5)
    .map((entry) => ({ name: String(entry.name || ''), value: toFiniteNumber(entry.value) ?? 0 }))
    .filter((entry) => entry.name);
  if (entries.length === 0) return null;
  const max = Math.max(...entries.map((entry) => Math.abs(entry.value)), 1);

  return (
    <div className="mt-2 rounded-[8px] border border-[#EDE6E1] bg-white px-3 py-2">
      {visual.label && (
        <div className="mb-1.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
          {String(visual.label)}
        </div>
      )}
      <ul className="space-y-1">
        {entries.map((entry) => (
          <li key={entry.name} className="grid grid-cols-[1fr_minmax(56px,120px)_70px] items-center gap-2 text-[11.5px]">
            <span className="truncate text-[#3F3338]">{entry.name}</span>
            <div className="h-[6px] overflow-hidden rounded-full bg-[#EDE5D8]/80">
              <div className="h-full rounded-full bg-[#4E7C89]" style={{ width: `${Math.max(2, Math.min(100, (Math.abs(entry.value) / max) * 100))}%` }} />
            </div>
            <span className="truncate text-right font-mono text-[#3F3338]">{formatLensValue(entry.value, visual.format)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LensVisual({ visual }: { visual?: unknown }) {
  if (!visual || typeof visual !== 'object') return null;
  const v = visual as LensItem;
  const kind = String(v.kind || '').toLowerCase();
  if (kind === 'spark') return <MiniSpark visual={v} />;
  if (kind === 'delta') return <DeltaVisual visual={v} />;
  if (kind === 'minibar') return <MinibarVisual visual={v} />;
  return null;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7B6B72]">
      {children}
    </div>
  );
}

function KeyNumbersGrid({ entries }: { entries: Array<[string, unknown]> }) {
  // Value-dominant tile grid. Values are large + mono + tabular so they
  // scan as numbers; raw keys sit underneath as small footnotes (a
  // payload reference, not a pretend-label). Two columns at sm+,
  // collapsing to one on narrow.
  if (entries.length === 0) return null;
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {entries.map(([k, v]) => (
        <li
          key={k}
          className="flex min-w-0 flex-col rounded-[8px] border border-[#EDE6E1] bg-white/70 px-3 py-2"
        >
          <span className="font-mono text-[15px] font-semibold tabular-nums leading-tight text-[#1F1A20]">
            {formatSupportingValueGeneric(v)}
          </span>
          <span
            className="mt-1 truncate font-mono text-[10px] leading-tight text-[#A8989F]"
            title={k}
          >
            {k}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DataUsedRow({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-[68px_1fr] items-baseline gap-3 text-[11px] leading-relaxed">
      <span className="font-display font-semibold uppercase tracking-[0.14em] text-[#A8989F]">
        {label}
      </span>
      <span className="font-mono text-[10.5px] text-[#5A4E54]">
        {items.join('  ·  ')}
      </span>
    </div>
  );
}

function LensBullet({ kind, item }: { kind: LensKind; item: LensItem }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = bucketConfig[kind];

  const point = String(item.point || item.summary || item.text || item.description || '').trim();
  const rationale = String(item.rationale || '').trim();

  const supportingNumbers: Record<string, unknown> =
    item.supporting_numbers && typeof item.supporting_numbers === 'object'
      ? (item.supporting_numbers as Record<string, unknown>)
      : {};
  const supportingEntries = Object.entries(supportingNumbers).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  const dimensions: string[] = Array.isArray(item.dimensions)
    ? item.dimensions.map(String).filter(Boolean)
    : [];
  const measures: string[] = Array.isArray(item.measures)
    ? item.measures.map(String).filter(Boolean)
    : [];
  const fields: string[] = Array.isArray(item.fields_used)
    ? item.fields_used.map(String).filter(Boolean)
    : item.field_citation
      ? [String(item.field_citation)]
      : [];

  const chipValue = toFiniteNumber(item.usd_value ?? item.usd_at_stake);
  const hasKeyNumbers = supportingEntries.length > 0;
  const hasDataUsed = dimensions.length > 0 || measures.length > 0 || fields.length > 0;
  const hasExpandable = Boolean(rationale) || hasKeyNumbers || hasDataUsed;

  if (!point) return null;

  const lever = kind === 'watchouts' && item.lever ? String(item.lever) : '';
  const plan = kind === 'risks' && item.plan ? String(item.plan) : '';
  const detailId = `lens-detail-${kind}-${String(item.id ?? item.point ?? 'item').slice(0, 40)}`;

  return (
    <li
      className={cn(
        'group/lens-bullet relative overflow-hidden rounded-[10px] border border-[#EDE6E1] bg-[#FBF9F4]/40',
        'transition-[background-color,border-color] duration-150',
        'hover:border-[#E1D6C7] hover:bg-[#FBF9F4]/80',
        expanded && 'bg-[#FBF9F4] shadow-[0_1px_0_0_rgba(31,26,32,0.04)]'
      )}
    >
      {/* Left accent stripe — kind color, full height */}
      <span className={cn('absolute inset-y-0 left-0 w-[3px]', cfg.stripe)} aria-hidden="true" />

      <div className="px-4 py-3.5 pl-5">
        {/* Header row: headline + value chip */}
        <div className="flex items-start gap-3">
          <h4 className="min-w-0 flex-1 font-display text-[15px] font-medium leading-snug text-[#1F1A20]">
            {point}
          </h4>
          {chipValue !== null && (
            <span
              className={cn(
                'inline-flex shrink-0 items-baseline rounded-[7px] px-2 py-[3px] font-mono text-[12px] font-semibold tabular-nums',
                cfg.chipBg,
                cfg.chipText
              )}
              title={kind === 'strengths' ? 'Value protected' : 'Value at stake'}
            >
              {formatLensValue(item.usd_value ?? item.usd_at_stake, 'USD')}
            </span>
          )}
        </div>

        {/* Rationale — Markdown-authored by the curator. Visible by
            default with a 2-line clamp; full content on expand. We
            override MarkdownContent's default paragraph margins to
            zero so the ``-webkit-line-clamp`` measures clean lines
            without the prose-style ``my-3`` gaps inflating each
            paragraph's footprint. */}
        {rationale && (
          <div
            className={cn(
              'mt-2',
              !expanded && 'line-clamp-2'
            )}
          >
            <MarkdownContent
              content={rationale}
              className={cn(
                'text-[12.5px] leading-relaxed text-[#3F3338]',
                '[&_p]:my-0 [&_p]:text-[12.5px] [&_p]:leading-relaxed',
                '[&_ul]:my-1 [&_ol]:my-1',
                '[&_strong]:text-[#1F1A20]'
              )}
            />
          </div>
        )}

        {/* Lever / Plan — kept for any future backend additions. */}
        {lever && (
          <p className="mt-2.5 text-[12px] text-[#5A4E54]">
            <span className="mr-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
              Lever
            </span>
            <span className="italic">{lever}</span>
          </p>
        )}
        {plan && (
          <p className="mt-2.5 text-[12px] text-[#5A4E54]">
            <span className="mr-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
              Plan
            </span>
            <span className="italic">{plan}</span>
          </p>
        )}

        <LensVisual visual={item.visual} />

        {/* Single "Read more" gate. Collapses to a clamped rationale;
            expands to show the full rationale plus two clearly titled
            sections: Key Numbers (supporting figures) and Data Used
            (raw payload references, framed honestly as audit IDs). */}
        {hasExpandable && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={cn(
                'mt-3 inline-flex items-center gap-1.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.14em] transition-colors',
                expanded ? cfg.expandedAccent : 'text-[#7B6B72] hover:text-[#3F3338]'
              )}
              aria-expanded={expanded}
              aria-controls={detailId}
            >
              <span>{expanded ? 'Show less' : 'Read more'}</span>
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform duration-200 ease-out',
                  expanded && 'rotate-180'
                )}
                strokeWidth={2.5}
              />
            </button>

            <div
              id={detailId}
              className={cn(
                'grid transition-all duration-200 ease-out',
                expanded ? 'mt-3 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              )}
              aria-hidden={!expanded}
            >
              <div className="overflow-hidden">
                <div className="space-y-4 border-t border-dashed border-[#E5DACC] pt-3">
                  {hasKeyNumbers && (
                    <section>
                      <SectionTitle>Key Numbers</SectionTitle>
                      <KeyNumbersGrid entries={supportingEntries} />
                    </section>
                  )}

                  {hasDataUsed && (
                    <section>
                      <SectionTitle>Data Used</SectionTitle>
                      <p className="mb-2 text-[10.5px] italic leading-snug text-[#A8989F]">
                        Raw references from the source system — identifiers, not display labels.
                      </p>
                      <div className="space-y-1">
                        <DataUsedRow label="Slices" items={dimensions} />
                        <DataUsedRow label="Measures" items={measures} />
                        <DataUsedRow label="Columns" items={fields} />
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  );
}

function Bucket({ kind, items }: { kind: LensKind; items: LensItem[] }) {
  const cfg = bucketConfig[kind];
  const Icon = cfg.Icon;
  const count = items.length;

  return (
    <div className="relative flex min-w-0 flex-col">
      <div className={cn('mb-3 h-[3px] w-12 rounded-full', cfg.rule)} />
      <header className="mb-1 flex items-center gap-2">
        <Icon className={cn('h-3.5 w-3.5', cfg.icon)} />
        <span className={cn('font-display text-[11px] font-semibold uppercase tracking-[0.14em]', cfg.icon)}>
          {cfg.title}
        </span>
        {count > 0 && (
          <span
            className={cn(
              'ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-mono text-[10.5px] font-semibold tabular-nums',
              cfg.countBg,
              cfg.countText
            )}
            aria-label={`${count} ${count === 1 ? 'item' : 'items'}`}
          >
            {count}
          </span>
        )}
      </header>
      <p className="mb-3 text-[11px] italic leading-relaxed text-[#7B6B72]">{cfg.blurb}</p>
      {count === 0 ? (
        <div className="rounded-[8px] border border-dashed border-[#EDE5D8] bg-[#FBF9F4]/40 px-3 py-2.5">
          <p className="text-[12px] italic text-[#A8989F]">None material in this view.</p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((item, index) => (
            <LensBullet key={String(item.id || `${kind}-${index}`)} kind={kind} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function PerformanceLensSection({ data, title = 'Performance lens', framed = false }: PerformanceLensSectionProps) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const buckets = bucketItems(data);
  const isEmpty = !buckets.strengths.length && !buckets.watchouts.length && !buckets.risks.length;
  if (isEmpty) return null;

  const shouldStackBuckets = width === null || width < PERFORMANCE_LENS_STACK_WIDTH;
  const orderedBuckets: Array<{ kind: LensKind; items: LensItem[] }> = [
    { kind: 'strengths', items: buckets.strengths },
    { kind: 'watchouts', items: buckets.watchouts },
    { kind: 'risks', items: buckets.risks },
  ];
  const content = (
    <div
      ref={ref}
      className={cn(
        'grid min-w-0 gap-6',
        // V2-only: stack buckets only when the measured lens area is genuinely compact,
        // not merely because the nav/chat panels are open or collapsed.
        shouldStackBuckets ? 'grid-cols-1' : 'grid-cols-3 divide-x divide-[#EDE5D8]'
      )}
    >
      {orderedBuckets.map(({ kind, items }, index) => (
        <div
          key={kind}
          className={cn(
            'min-w-0',
            shouldStackBuckets
              ? index > 0 && 'border-t border-[#EDE5D8] pt-6'
              : index === 0
                ? 'pr-6'
                : index === orderedBuckets.length - 1
                  ? 'pl-6'
                  : 'px-6'
          )}
        >
          <Bucket kind={kind} items={items} />
        </div>
      ))}
    </div>
  );

  if (framed) {
    return <FinanceV2Section title={title}>{content}</FinanceV2Section>;
  }

  return content;
}
